import { ArrowLeft, Heart, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { DestinationCard, RegionCard, regionOfferCount } from "../components/Cards";
import { TravelAgencyNotice } from "../components/Disclosures";
import { usePageHeader } from "../components/shell/PageHeaderContext";
import { useGuest } from "../components/shell/GuestShell";
import { Spinner } from "../components/Spinner";
import { TripProductCard } from "../components/trips/TripProductCard";
import { CustomTripBand, CustomTripInline } from "../components/trips/CustomTripPrompt";
import { useDiscover, useSavedTrips, useTrips } from "../lib/queries";
import { tripMatchesCategory, tripMatchesQuery } from "@aventuria/client-core/trip-search";
import type { Destination, DestinationCategory, Region, TripCard } from "../types";

const FILTERS: Array<{ id: DestinationCategory | "todos"; label: string }> = [
  { id: "todos", label: "Todos" },
  { id: "playa", label: "Playa" },
  { id: "ciudad", label: "Ciudad" },
  { id: "aventura", label: "Aventura" },
  { id: "cultura", label: "Cultura" }
];

function normalize(value: string) {
  return value.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

/** "Explora" (Desktop · Discover): search, category chips, regions, and a region's destinations. */
export function DiscoverPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const regions = useDiscover();
  const trips = useTrips();
  const { guest, askForAccount } = useGuest();
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<DestinationCategory | "todos">("todos");
  const inputRef = useRef<HTMLInputElement>(null);

  const region = slug ? regions.data?.find((item) => item.slug === slug) : undefined;
  usePageHeader(region ? region.name : "Viajes", region
    ? region.subtitle ?? `${region.destinationCount} destinos`
    : "Empieza por una región — luego elegimos juntos el viaje perfecto.");

  // ⌘K / Ctrl+K and the topbar search button land in the search field.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") { event.preventDefault(); inputRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  useEffect(() => {
    if (searchParams.get("buscar")) {
      inputRef.current?.focus();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const matches = (destination: Destination, parent: Region) => {
    if (filter !== "todos" && destination.category !== filter) return false;
    if (!query.trim()) return true;
    const needle = normalize(query);
    return [destination.name, destination.country, destination.subtitle ?? "", parent.name, destination.category, ...destination.perks]
      .some((value) => normalize(value).includes(needle));
  };

  // Trips follow the same search and category filter as destinations (E3): a trip shows under the
  // categories it is tagged with or its linked destination's; the search also reads its countries'
  // English names and its keywords (client-core/trip-search).
  const visibleTrips = useMemo(() => {
    return (trips.data ?? []).filter((trip) => tripMatchesCategory(trip, filter) && tripMatchesQuery(trip, query));
  }, [trips.data, query, filter]);
  const tripsByRegion = useMemo(() => {
    const map = new Map<string, TripCard[]>();
    for (const trip of visibleTrips) if (trip.region) map.set(trip.region.id, [...(map.get(trip.region.id) ?? []), trip]);
    return map;
  }, [visibleTrips]);
  const tripForDestination = (destination: Destination) => (trips.data ?? []).find((trip) => trip.destination?.id === destination.id);

  const visibleRegions = useMemo(() => (regions.data ?? [])
    .map((item) => ({ region: item, destinations: item.destinations.filter((destination) => matches(destination, item)) }))
    .filter(({ destinations, region: item }) => destinations.length > 0 || (tripsByRegion.get(item.id)?.length ?? 0) > 0 || (!query.trim() && filter === "todos" && item.destinationCount === 0)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [regions.data, query, filter, tripsByRegion]);

  const searching = query.trim().length > 0 || filter !== "todos";

  const startSaving = (destination: Destination) => {
    if (guest) { askForAccount(`empezar a ahorrar para ${destination.name}`); return; }
    navigate(`/wallet/libre/nueva?destino=${encodeURIComponent(destination.slug)}`);
  };

  if (regions.isLoading) return <div className="page-state"><Spinner label="Cargando destinos" /><span>Cargando destinos</span></div>;
  if (regions.isError) return <div className="page-state error"><strong>No pudimos cargar los destinos</strong><button className="secondary-button" onClick={() => void regions.refetch()}>Reintentar</button></div>;

  if (slug && !region) {
    return <div className="page-state"><strong>Región no encontrada</strong><button className="secondary-button" onClick={() => navigate("/explorar")}>Volver a Viajes</button></div>;
  }

  if (region) {
    const destinations = region.destinations.filter((destination) => matches(destination, region));
    return (
      <div className="page-container">
        <button type="button" className="back-button" onClick={() => navigate("/explorar")}><ArrowLeft size={16} aria-hidden="true" /> Todas las regiones</button>
        <div className="discover-toolbar">
          <div className="discover-search">
            <label className="search-field">
              <Search size={18} aria-hidden="true" />
              <input ref={inputRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Buscar en ${region.name}`} aria-label={`Buscar en ${region.name}`} />
              <kbd>⌘K</kbd>
            </label>
            <SavedTripsLink />
          </div>
          <Chips value={filter} onChange={setFilter} />
        </div>
        {(tripsByRegion.get(region.id) ?? []).length > 0 && (
          <>
            <span className="rail-title section-eyebrow">Viajes en {region.name} · {tripsByRegion.get(region.id)!.length}</span>
            <div className="trip-card-grid">
              {tripsByRegion.get(region.id)!.map((trip) => <TripProductCard key={trip.id} trip={trip} onClick={() => navigate(`/viajes/${trip.slug}`)} />)}
            </div>
          </>
        )}
        {/* A region may be only trips (no destination cards): its Destinos section then does not show. */}
        {region.destinations.length > 0 && (
          <>
            <span className={`rail-title section-eyebrow${(tripsByRegion.get(region.id) ?? []).length ? " section-divider" : ""}`}>Destinos · {destinations.length}</span>
            {destinations.length > 0 && (
              <div className="dest-grid">{destinations.map((destination) => {
                const trip = tripForDestination(destination);
                return trip
                  ? <DestinationCard key={destination.id} destination={destination} action="Ver viaje" onClick={() => navigate(`/viajes/${trip.slug}`)} />
                  : <DestinationCard key={destination.id} destination={destination} onClick={() => startSaving(destination)} />;
              })}</div>
            )}
          </>
        )}
        {destinations.length === 0 && (tripsByRegion.get(region.id) ?? []).length === 0 && (
          <div className="empty-state"><strong>Sin viajes con ese filtro</strong><span>Prueba otra categoría o búsqueda.</span></div>
        )}
        <CustomTripInline
          placement="explore_region"
          title={`¿Buscas otra cosa en ${region.name}?`}
          text="Dinos a dónde y cuándo: diseñamos el viaje contigo y un especialista te contacta en 24 a 48 h hábiles."
          prefill={{ destinations: [query.trim() || region.name] }}
        />
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="discover-toolbar">
        <div className="discover-search">
          <label className="search-field">
            <Search size={18} aria-hidden="true" />
            <input ref={inputRef} type="search" value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Buscar país, ciudad o experiencia" aria-label="Buscar país, ciudad o experiencia" />
            <kbd>⌘K</kbd>
          </label>
          <SavedTripsLink />
        </div>
        <Chips value={filter} onChange={setFilter} />
      </div>

      <span className="rail-title section-eyebrow">Por región</span>
      {visibleRegions.length ? (
        <div className="region-grid">
          {visibleRegions.map(({ region: item, destinations }) => (
            <RegionCard key={item.id} region={item} count={regionOfferCount(item, tripsByRegion.get(item.id) ?? [], destinations)} onClick={() => navigate(`/explorar/${item.slug}`)} />
          ))}
        </div>
      ) : (
        <div className="empty-state"><strong>Sin resultados</strong><span>No encontramos destinos para “{query}”.</span></div>
      )}

      {/* Viaje a la medida (D1): before the trips while browsing; after the results when searching,
          carrying what the customer searched for. */}
      {!searching && <CustomTripBand placement="explore_band" />}

      {/* Published trip products (E3). */}
      <span className="rail-title section-eyebrow section-divider">Viajes{trips.data?.length ? ` · ${visibleTrips.length}` : ""}</span>
      {visibleTrips.length > 0 && (
        <div className="trip-card-grid">
          {visibleTrips.map((trip) => <TripProductCard key={trip.id} trip={trip} onClick={() => navigate(`/viajes/${trip.slug}`)} />)}
        </div>
      )}
      {searching && (
        <CustomTripInline
          placement={visibleTrips.length ? "explore_search" : "explore_no_results"}
          title={visibleTrips.length ? "¿No es exactamente lo que buscas?" : query.trim() ? `Aún no tenemos un viaje para «${query.trim()}»` : "Ningún viaje coincide con ese filtro"}
          text="Cuéntanos qué tienes en mente y un especialista te propone un viaje a la medida, sin costo ni compromiso."
          prefill={{ destinations: query.trim() ? [query.trim()] : [], note: filter !== "todos" ? `Me interesa un viaje de tipo ${FILTERS.find((option) => option.id === filter)?.label.toLowerCase()}.` : undefined }}
        />
      )}

      {searching && visibleRegions.some(({ destinations }) => destinations.length > 0) && (
        <>
          <span className="rail-title section-eyebrow section-divider">Destinos</span>
          <div className="dest-grid">
            {visibleRegions.flatMap(({ destinations }) => destinations).map((destination) => {
              const trip = tripForDestination(destination);
              return trip
                ? <DestinationCard key={destination.id} destination={destination} action="Ver viaje" onClick={() => navigate(`/viajes/${trip.slug}`)} />
                : <DestinationCard key={destination.id} destination={destination} onClick={() => startSaving(destination)} />;
            })}
          </div>
        </>
      )}

      <TravelAgencyNotice />
    </div>
  );
}

function Chips({ value, onChange }: { value: DestinationCategory | "todos"; onChange(next: DestinationCategory | "todos"): void }) {
  return (
    <div className="chip-row" role="group" aria-label="Filtrar por tipo de viaje">
      {FILTERS.map((option) => (
        <button key={option.id} type="button" className={`chip${value === option.id ? " active" : ""}`} aria-pressed={value === option.id} onClick={() => onChange(option.id)}>
          {option.label}
        </button>
      ))}
    </div>
  );
}

/**
 * Viajes guardados belong to Explorar (the collection of trips the customer is considering), so
 * they open from its toolbar — the heart with the count — rather than from the main navigation.
 */
function SavedTripsLink() {
  const navigate = useNavigate();
  const { guest, askForAccount } = useGuest();
  const saved = useSavedTrips({ enabled: !guest });
  const count = saved.data?.length ?? 0;
  return (
    <button
      type="button" className="saved-square"
      onClick={() => (guest ? askForAccount("guardar viajes y recibir avisos de precio") : navigate("/guardados"))}
      aria-label={count ? `Viajes guardados · ${count}` : "Viajes guardados"} title="Viajes guardados"
    >
      <Heart size={19} aria-hidden="true" fill={count ? "currentColor" : "none"} />
      {count > 0 && <span className="saved-square-count" translate="no">{count}</span>}
    </button>
  );
}
